import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, Activity, FileText, TrendingUp, AlertCircle } from "lucide-react";
import { SiteFeatureGate } from "@/components/trial-site/SiteFeatureGate";
import { Badge } from "@/components/ui/badge";
import { useSitePatientStats, SitePatientStats } from "@/hooks/useSitePatientStats";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// Safe accessor helpers to prevent runtime crashes
function safeToFixed(value: number | undefined | null, decimals: number = 1): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '0.0';
  }
  return value.toFixed(decimals);
}

function safeObjectKeys(obj: Record<string, unknown> | undefined | null): string[] {
  if (!obj || typeof obj !== 'object') {
    return [];
  }
  return Object.keys(obj);
}

// Stats display component for better organization
function StatsContent({ stats }: { stats: SitePatientStats }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* OMOP Mapping Stats */}
      <div className="p-4 border rounded-lg bg-card">
        <div className="flex items-center gap-2 mb-2">
          <Activity className="h-5 w-5 text-teal-500" />
          <h3 className="font-semibold">OMOP Mapping</h3>
        </div>
        <div className="space-y-1">
          <p className="text-2xl font-bold">{stats.omopMappedCount}</p>
          <p className="text-sm text-muted-foreground">
            of {stats.totalPatients} patients mapped
          </p>
          <div className="flex items-center gap-2 mt-2">
            <TrendingUp className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium">
              {safeToFixed(stats.omopMappingRate)}% complete
            </span>
          </div>
        </div>
      </div>

      {/* Diversity Stats */}
      <div className="p-4 border rounded-lg bg-card">
        <div className="flex items-center gap-2 mb-2">
          <Users className="h-5 w-5 text-purple-500" />
          <h3 className="font-semibold">Diversity Data</h3>
        </div>
        <div className="space-y-1">
          <p className="text-2xl font-bold">
            {safeObjectKeys(stats.diversityBreakdown?.race).length}
          </p>
          <p className="text-sm text-muted-foreground">
            race categories tracked
          </p>
          <p className="text-sm font-medium mt-2">
            {safeObjectKeys(stats.diversityBreakdown?.ethnicity).length} ethnicity categories
          </p>
        </div>
      </div>

      {/* Medical Conditions Stats */}
      <div className="p-4 border rounded-lg bg-card">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5 text-blue-500" />
          <h3 className="font-semibold">Medical Conditions</h3>
        </div>
        <div className="space-y-1">
          <p className="text-2xl font-bold">{stats.medicalConditionsCount}</p>
          <p className="text-sm text-muted-foreground">
            total conditions documented
          </p>
          <p className="text-sm font-medium mt-2">
            {stats.totalPatients} patients
          </p>
        </div>
      </div>
    </div>
  );
}

// Loading skeleton component
function StatsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
    </div>
  );
}

// Error display component
function StatsError() {
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Unable to load statistics</AlertTitle>
      <AlertDescription>
        We're having trouble loading site statistics. The core tools below remain available.
      </AlertDescription>
    </Alert>
  );
}

export default function SitePatientManagementPro() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { data: stats, isLoading, isError } = useSitePatientStats(siteId || '');

  if (!siteId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <p className="text-center">Site ID is required</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title="Patient Management Pro - Clinical Tier"
        description="Complete OMOP mapping, diversity demographics, and detailed medical conditions"
        url={`/trial-site/${siteId}/patient-management`}
      />

      <SiteFeatureGate siteId={siteId} featureName="omop_sdtm_export">
        <div className="container mx-auto px-4 py-8 pb-24 md:pb-8">
          {/* Header - Always renders immediately */}
          <div className="mb-6">
            <Button variant="ghost" onClick={() => navigate(`/trial-site/${siteId}`)} className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Site Dashboard
            </Button>
            
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-bold">Patient Management Pro</h1>
              <Badge variant="secondary">Clinical Tier</Badge>
            </div>
            <p className="text-muted-foreground">
              Complete OMOP mapping, diversity demographics, and detailed medical conditions
            </p>
          </div>

          {/* Feature Cards - Always render immediately */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* OMOP Mapping Card */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-teal-500" />
                  OMOP Mapping
                </CardTitle>
                <CardDescription>
                  Complete OMOP CDM mapping for all patient data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Map patient data to OMOP Common Data Model standards for research and regulatory compliance.
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate(`/trial-site/${siteId}/omop`)}
                >
                  View OMOP Dashboard
                </Button>
              </CardContent>
            </Card>

            {/* Diversity Demographics Card */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-500" />
                  Diversity Demographics
                </CardTitle>
                <CardDescription>
                  Detailed diversity data for trial enrollment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Capture comprehensive diversity markers including genetic ancestry, ethnicity, and geographic origin.
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate(`/trial-site/${siteId}/diversity`)}
                >
                  Manage Demographics
                </Button>
              </CardContent>
            </Card>

            {/* Medical Conditions Card */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  Medical Conditions
                </CardTitle>
                <CardDescription>
                  ICD-10 coded medical history
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Complete medical history with ICD-10 codes, medications, allergies, and treatment history.
                </p>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate(`/trial-site/${siteId}/conditions`)}
                >
                  View Conditions
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Site-Level Aggregate Stats - Graceful loading/error states */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Site Overview Statistics</CardTitle>
              <CardDescription>
                Aggregate data quality and diversity metrics for your patient cohort
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <StatsSkeleton />
              ) : isError ? (
                <StatsError />
              ) : stats ? (
                <StatsContent stats={stats} />
              ) : (
                <StatsSkeleton />
              )}
            </CardContent>
          </Card>
        </div>
      </SiteFeatureGate>
    </Layout>
  );
}
